import { useState, useEffect } from "react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Calendar, Download, RefreshCw, TrendingUp, TrendingDown, BarChart3 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PerformanceData {
  portfolio: string;
  period: string;
  portfolioReturn: number;
  benchmarkReturn: number;
  secondaryBenchmarkReturn?: number;
  excessReturn: number;
  secondaryExcessReturn?: number;
}

const InternalDistribution = () => {
  const [performanceData, setPerformanceData] = useState<PerformanceData[]>([]);
  const [availablePortfolios, setAvailablePortfolios] = useState<any[]>([]);
  const [portfolioBenchmarks, setPortfolioBenchmarks] = useState<any[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<string>("all");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Fetch portfolios and benchmarks from database
  const fetchPortfolios = async () => {
    try {
      // Fetch both portfolio ranges and individual portfolios
      const [rangesResponse, individualsResponse, benchmarksResponse] = await Promise.all([
        supabase.from('portfolio_ranges').select('*').eq('status', 'active').order('sort_order'),
        supabase.from('individual_portfolios').select('*, range_id').eq('status', 'active').order('sort_order'),
        supabase.from('portfolio_benchmarks').select('*').order('display_order')
      ]);

      const portfolios = [
        ...(rangesResponse.data || []).map(p => ({ ...p, type: 'range' })),
        ...(individualsResponse.data || []).map(p => ({ ...p, type: 'individual' }))
      ];

      setAvailablePortfolios(portfolios);
      setPortfolioBenchmarks(benchmarksResponse.data || []);
    } catch (error) {
      console.error('Error fetching portfolios:', error);
    }
  };

  // Fetch performance data
  const fetchPerformanceData = async () => {
    setLoading(true);
    try {
      let portfoliosToFetch: string[] = [];
      
      if (selectedPortfolio === "all") {
        // Fetch data for all individual portfolios
        portfoliosToFetch = availablePortfolios
          .filter(p => p.type === 'individual')
          .map(p => p.slug);
      } else {
        // Check if selected portfolio is a range
        const selectedPortfolioObj = availablePortfolios.find(p => p.slug === selectedPortfolio);
        
        if (selectedPortfolioObj?.type === 'range') {
          // Fetch data for all individual portfolios in this range
          portfoliosToFetch = availablePortfolios
            .filter(p => p.type === 'individual' && p.range_id === selectedPortfolioObj.id)
            .map(p => p.slug);
        } else {
          // Fetch data for single individual portfolio
          portfoliosToFetch = [selectedPortfolio];
        }
      }

      console.log('Fetching data for portfolios:', portfoliosToFetch);
      
      // Fetch data for all portfolios
      const allPerformanceData: PerformanceData[] = [];
      
      for (const portfolioSlug of portfoliosToFetch) {
        const { data, error } = await supabase.functions.invoke('internal-distribution-data', {
          body: {
            portfolio: portfolioSlug,
            timeframe: "all",
            availablePortfolios: availablePortfolios,
            portfolioBenchmarks: portfolioBenchmarks
          }
        });

        if (error) {
          console.error(`Error fetching performance data for ${portfolioSlug}:`, error);
          continue;
        }

        if (data?.data) {
          allPerformanceData.push(...data.data);
        }
      }

      console.log('All performance data received:', allPerformanceData);
      setPerformanceData(allPerformanceData);
      
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to fetch performance data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPortfolios();
  }, []);

  useEffect(() => {
    if (availablePortfolios.length > 0 && portfolioBenchmarks.length > 0) {
      fetchPerformanceData();
    }
  }, [selectedPortfolio, availablePortfolios, portfolioBenchmarks]);

  const getFilteredData = (timeframe: string) => {
    let filteredData = performanceData;
    
    // Filter out p1-portfolio entries
    filteredData = filteredData.filter(data => data.portfolio !== 'p1-portfolio');
    
    if (timeframe === "all") return filteredData;
    return filteredData.filter(data => data.period === timeframe);
  };

  const getPortfolioName = (code: string) => {
    const portfolio = availablePortfolios.find(p => 
      p.slug === code || p.name === code || 
      (p.type === 'individual' && p.slug === code)
    );
    return portfolio ? portfolio.name : code;
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const getPerformanceBadge = (value: number) => {
    if (value > 0) {
      return <Badge variant="default" className="bg-green-100 text-green-800">{formatPercentage(value)}</Badge>;
    } else if (value < 0) {
      return <Badge variant="destructive">{formatPercentage(value)}</Badge>;
    } else {
      return <Badge variant="secondary">{formatPercentage(value)}</Badge>;
    }
  };

  const getExcessBadge = (value: number) => {
    if (value > 0) {
      return <Badge variant="default" className="bg-blue-100 text-blue-800">+{value.toFixed(2)}%</Badge>;
    } else if (value < 0) {
      return <Badge variant="outline" className="bg-red-50 text-red-700">{value.toFixed(2)}%</Badge>;
    } else {
      return <Badge variant="secondary">0.00%</Badge>;
    }
  };

  const renderPerformanceTable = (data: PerformanceData[], timeframeName: string) => {

    return (
      <div className="space-y-6">

        {/* Performance Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{timeframeName} Performance Analysis</CardTitle>
                <CardDescription>
                  Portfolio performance vs benchmark comparison
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <Button variant="outline" size="sm" onClick={fetchPerformanceData} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Portfolio</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Portfolio Return</TableHead>
                  <TableHead className="text-right">Primary Benchmark</TableHead>
                  <TableHead className="text-right">Excess vs Primary</TableHead>
                  <TableHead className="text-right">Secondary Benchmark</TableHead>
                  <TableHead className="text-right">Excess vs Secondary</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      {loading ? "Loading performance data..." : "No performance data available"}
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((record, index) => {
                    // Get benchmark names for this portfolio
                    const portfolioBenchmarkConfigs = portfolioBenchmarks.filter(b => 
                      b.portfolio_code === record.portfolio
                    ).sort((a, b) => a.display_order - b.display_order);
                    
                    const primaryBenchmark = portfolioBenchmarkConfigs.find(b => b.benchmark_type === 'primary') || portfolioBenchmarkConfigs[0];
                    const secondaryBenchmark = portfolioBenchmarkConfigs.find(b => b.benchmark_type === 'secondary');
                    
                    return (
                      <TableRow key={`${record.portfolio}-${record.period}-${index}`}>
                        <TableCell>
                          <div>
                            <span className="font-medium">{getPortfolioName(record.portfolio)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{record.period}</TableCell>
                        <TableCell className="text-right">
                          {getPerformanceBadge(record.portfolioReturn)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div>
                            {getPerformanceBadge(record.benchmarkReturn)}
                            {primaryBenchmark && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {primaryBenchmark.benchmark_short_name || primaryBenchmark.benchmark_name}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div>
                            {getExcessBadge(record.excessReturn)}
                            {primaryBenchmark?.excess_label && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {primaryBenchmark.excess_label}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {record.secondaryBenchmarkReturn !== undefined ? (
                            <div>
                              {getPerformanceBadge(record.secondaryBenchmarkReturn)}
                              {secondaryBenchmark && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {secondaryBenchmark.benchmark_short_name || secondaryBenchmark.benchmark_name}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {record.secondaryExcessReturn !== undefined ? (
                            <div>
                              {getExcessBadge(record.secondaryExcessReturn)}
                              {secondaryBenchmark?.excess_label && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {secondaryBenchmark.excess_label}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <>
      <div className="container mx-auto py-8 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Portfolio Performance Analysis</h1>
            <p className="text-muted-foreground">
              Compare portfolio performance against benchmarks across different time periods
            </p>
          </div>
          <BarChart3 className="h-8 w-8 text-primary" />
        </div>

        {/* Portfolio Filter */}
        <Card>
          <CardHeader>
            <CardTitle>Portfolio Selection</CardTitle>
            <CardDescription>
              Select a specific portfolio to analyze or view all portfolios
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Label htmlFor="portfolio-select">Portfolio:</Label>
              <Select value={selectedPortfolio} onValueChange={setSelectedPortfolio}>
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder="Select a portfolio" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Portfolios</SelectItem>
                  {availablePortfolios.map((portfolio) => (
                    <SelectItem key={portfolio.id} value={portfolio.slug}>
                      {portfolio.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="all" className="space-y-6">
          <TabsList className="grid w-full grid-cols-8">
            <TabsTrigger value="all">All Periods</TabsTrigger>
            <TabsTrigger value="1m">1 Month</TabsTrigger>
            <TabsTrigger value="3m">3 Months</TabsTrigger>
            <TabsTrigger value="1yr">1 Year</TabsTrigger>
            <TabsTrigger value="3yr">3 Years</TabsTrigger>
            <TabsTrigger value="YTD">YTD</TabsTrigger>
            <TabsTrigger value="Since Inception">Since Inception</TabsTrigger>
            <TabsTrigger value="Since P/R">Since P/R</TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            {renderPerformanceTable(getFilteredData("all"), "All Periods")}
          </TabsContent>

          <TabsContent value="1m">
            {renderPerformanceTable(getFilteredData("1m"), "1 Month")}
          </TabsContent>

          <TabsContent value="3m">
            {renderPerformanceTable(getFilteredData("3m"), "3 Months")}
          </TabsContent>

          <TabsContent value="1yr">
            {renderPerformanceTable(getFilteredData("1yr"), "1 Year")}
          </TabsContent>

          <TabsContent value="3yr">
            {renderPerformanceTable(getFilteredData("3yr"), "3 Years")}
          </TabsContent>

          <TabsContent value="YTD">
            {renderPerformanceTable(getFilteredData("YTD"), "Year to Date")}
          </TabsContent>

          <TabsContent value="Since Inception">
            {renderPerformanceTable(getFilteredData("Since Inception"), "Since Inception")}
          </TabsContent>

          <TabsContent value="Since P/R">
            {renderPerformanceTable(getFilteredData("Since P/R"), "Since P/R")}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
};

export default InternalDistribution;